import { useEffect, useMemo, useRef, useState } from 'react';

import { useListConnectors } from './use-fetch-connectors';

import type { Connector } from '@/types/core/connector';
import { flattenPages } from '@/types/shared/page';
import { usePollingRefetch } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

const EMPTY_CONFIRMATION_ATTEMPTS = 3;
const EMPTY_CONFIRMATION_POLL_DELAY_MS = 1000;
const DEBUG_LOADING_LOGS = import.meta.env.DEV;

/**
 * Determines whether a tenant genuinely has zero connectors by requiring
 * several consecutive empty refetches after the initial empty response.
 * This guards against eventual-consistency returning a stale empty list on
 * first load.
 *
 * Returns:
 * - `isLoading`: true while the initial fetch or confirmation refetch is in-flight.
 * - `hasNoConnectors`: true only after a confirmation refetch also returns empty.
 * - `connectors`: the non-internal connectors once loaded.
 * - The remaining fields pass through from `useListConnectors`.
 */
export function useConfirmedNoConnectors(
  tenantId: UUID,
  opts?: {
    enabled?: boolean;
  }
) {
  const isQueryEnabled = opts?.enabled ?? true;
  const query = useListConnectors(tenantId, {
    limit: 100,
    enabled: isQueryEnabled,
  });
  const { data, isPending, isError, dataUpdatedAt, refetch } = query;

  const currentUserConnectors = useMemo(
    () => flattenPages(data?.pages).filter((c: Connector) => !c.isInternal),
    [data]
  );

  const [confirmedEmpty, setConfirmedEmpty] = useState(false);
  const emptyConfirmationCountRef = useRef(0);
  const lastObservedEmptyUpdateAtRef = useRef<number>(0);
  const lastKnownNonEmptyConnectorsRef = useRef<Connector[]>(
    currentUserConnectors
  );

  let loadPhase = 'confirming-empty';
  if (!isQueryEnabled) {
    loadPhase = 'disabled';
  } else if (isPending) {
    loadPhase = 'initial-load';
  } else if (isError) {
    loadPhase = 'error';
  } else if (currentUserConnectors.length > 0) {
    loadPhase = 'connectors-found';
  } else if (confirmedEmpty) {
    loadPhase = 'confirmed-empty';
  } else if (lastKnownNonEmptyConnectorsRef.current.length > 0) {
    loadPhase = 'stale-empty-known';
  }

  useEffect(() => {
    lastKnownNonEmptyConnectorsRef.current = [];
    emptyConfirmationCountRef.current = 0;
    lastObservedEmptyUpdateAtRef.current = 0;
    setConfirmedEmpty(false);
  }, [tenantId, isQueryEnabled]);

  useEffect(() => {
    if (DEBUG_LOADING_LOGS) {
      console.info('[useConfirmedNoConnectors] state', {
        tenantId,
        loadPhase,
        isPending,
        isError,
        dataUpdatedAt,
        currentConnectorCount: currentUserConnectors.length,
        stableConnectorCount:
          confirmedEmpty || currentUserConnectors.length > 0
            ? currentUserConnectors.length
            : lastKnownNonEmptyConnectorsRef.current.length,
        confirmedEmpty,
        confirmationAttempts: emptyConfirmationCountRef.current,
      });
    }

    if (!isQueryEnabled || isPending || isError) {
      return;
    }

    if (currentUserConnectors.length > 0) {
      lastKnownNonEmptyConnectorsRef.current = currentUserConnectors;
      emptyConfirmationCountRef.current = 0;
      lastObservedEmptyUpdateAtRef.current = dataUpdatedAt;
      if (confirmedEmpty) {
        setConfirmedEmpty(false);
      }
      return;
    }

    if (confirmedEmpty) {
      return;
    }

    if (dataUpdatedAt === lastObservedEmptyUpdateAtRef.current) {
      return;
    }

    lastObservedEmptyUpdateAtRef.current = dataUpdatedAt;
    emptyConfirmationCountRef.current += 1;

    if (emptyConfirmationCountRef.current >= EMPTY_CONFIRMATION_ATTEMPTS) {
      setConfirmedEmpty(true);
    }
  }, [
    confirmedEmpty,
    dataUpdatedAt,
    isError,
    isQueryEnabled,
    isPending,
    loadPhase,
    tenantId,
    currentUserConnectors,
    currentUserConnectors.length,
  ]);

  const shouldConfirmEmpty =
    isQueryEnabled &&
    !isPending &&
    !isError &&
    currentUserConnectors.length === 0 &&
    !confirmedEmpty;

  usePollingRefetch(
    shouldConfirmEmpty,
    async () => {
      const startedAt = performance.now();
      const result = await refetch();

      if (DEBUG_LOADING_LOGS) {
        const pages = result.data?.pages ?? [];
        const items = flattenPages(pages).filter(
          (connector: Connector) => !connector.isInternal
        );
        console.info('[useConfirmedNoConnectors] confirmation refetch', {
          tenantId,
          durationMs: Math.round(performance.now() - startedAt),
          connectorCount: items.length,
          status: result.status,
          fetchStatus: result.fetchStatus,
          isError: result.isError,
          error: result.error?.message,
        });
      }

      return result;
    },
    EMPTY_CONFIRMATION_POLL_DELAY_MS
  );

  const hasKnownNonEmptyConnectors =
    lastKnownNonEmptyConnectorsRef.current.length > 0;

  const userConnectors =
    confirmedEmpty || currentUserConnectors.length > 0
      ? currentUserConnectors
      : lastKnownNonEmptyConnectorsRef.current;

  // Loading if the query itself is pending, or if we got empty and haven't
  // confirmed yet and don't have a known-good non-empty snapshot.
  const isLoading =
    isQueryEnabled &&
    (isPending ||
      (currentUserConnectors.length === 0 &&
        !confirmedEmpty &&
        !hasKnownNonEmptyConnectors &&
        !isError));

  return {
    ...query,
    userConnectors,
    isLoading,
    hasNoConnectors:
      isQueryEnabled && confirmedEmpty && currentUserConnectors.length === 0,
  };
}
